import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface ImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  bucket?: string;
  folder?: string;
  label?: string;
  placeholder?: string;
  previewShape?: 'square' | 'circle';
}

const ImageUploader = ({
  value,
  onChange,
  bucket = 'food-images',
  folder = 'uploads',
  label = 'Görsel',
  placeholder = 'https://...',
  previewShape = 'square',
}: ImageUploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Dosya 5MB\'dan büyük olamaz.');
      return;
    }
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setUploadError('Sadece JPG, PNG, WEBP veya GIF yükleyebilirsiniz.');
      return;
    }

    setUploading(true);
    setUploadError('');

    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, { upsert: false, contentType: file.type });

    if (error) {
      setUploadError('Yükleme başarısız: ' + error.message);
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(fileName);
    onChange(urlData.publicUrl);
    setUploading(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const previewClass = previewShape === 'circle' ? 'rounded-full' : 'rounded-xl';

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label className="block text-xs font-semibold text-gray-600">{label}</label>
      )}

      {/* URL input row */}
      <div className="flex gap-2 items-center">
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setUploadError(''); }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all"
        />
        {value && (
          <div className={`w-11 h-11 overflow-hidden border border-gray-200 flex-shrink-0 ${previewClass}`}>
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      {/* Upload area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 border-dashed transition-all cursor-pointer select-none ${
          dragOver
            ? 'border-orange-400 bg-orange-50'
            : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/40'
        } ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {uploading ? (
          <>
            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-xs text-gray-500">Yükleniyor...</span>
          </>
        ) : (
          <>
            <div className="w-8 h-8 flex items-center justify-center text-orange-400">
              <i className="ri-upload-cloud-2-line text-2xl"></i>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-700">
                Fotoğraf yükle
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Sürükle &amp; bırak veya tıkla · JPG, PNG, WEBP · Max 5MB
              </p>
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {uploadError && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          {uploadError}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
